// Основната колона на редактора: правилото като РАВЕНСТВО. Всеки продукт е
// карта с количество, картите са свързани с „=" и се четат като изречение
// („1 кг Кафе A = 0.7 кг Кафе B = 1.2 кг Кафе C"). След последната карта е
// следващото празно място с търсачката и „Целият списък"; при ново правило
// вторият продукт е призрачно място „Продукт 2", за да се види веднага какво
// е правило.
//
// При 3+ продукта се избира кой се изписва пръв: „Най-старата доставка"
// (без приоритет) или „По реда тук" — тогава картите носят номер, стрелки
// ↑/↓, Alt+↑/↓ и плъзгане (само добавка към клавиатурата).
//
// Клавиатура в количествата: ↓/Enter → следващото (от последното — в
// търсачката), ↑ → предишното; от търсачката ↑ се връща на последното.
import { Fragment, useState } from "react";
import type { DragEvent, KeyboardEvent, ReactNode } from "react";
import { useTranslation } from "react-i18next";
import type { Product } from "../../../../api/inventory";
import { KbdChip } from "../../components/KbdChip";
import { SegmentedCounts } from "../../components/list/ListBits";
import { useInventoryShell } from "../../components/shell/InventoryShellContext";
import { InterchangeMemberCard } from "./InterchangeMemberCard";
import { InterchangeRuleSearch } from "./InterchangeRuleSearch";
import type { PriorityMode } from "./interchangeRuleMath";
import { amountInputId, type InterchangeMemberDraft } from "./interchangeablesShared";

const moveButtonId = (key: number, direction: "up" | "down") => `ich-${direction}-${key}`;

// Фокус след преместване: картата сменя мястото си в DOM и браузърът може да
// изпусне фокуса — връщаме го на същия елемент (или на количеството, ако
// бутонът вече е неактивен — картата стигна ръба).
const refocus = (id: string, fallbackId: string) =>
  requestAnimationFrame(() => {
    const element = document.getElementById(id) as HTMLButtonElement | HTMLInputElement | null;
    const target = element && !element.disabled ? element : document.getElementById(fallbackId);
    target?.focus();
  });

type Props = {
  members: InterchangeMemberDraft[];
  categoryOf: (productId: number) => string | null;
  priorityMode: PriorityMode;
  sortIndexes: (number | null)[];
  onPriorityModeChange: (mode: PriorityMode) => void;
  onAmountChange: (key: number, value: number | null) => void;
  onMove: (key: number, toIndex: number) => void;
  onRemove: (key: number) => void;
  // търсачката в празното място
  availableProducts: Product[];
  search: string;
  onSearchChange: (value: string) => void;
  attachInputRef: (node: HTMLInputElement | null) => void;
  focusSearch: () => void;
  onAdd: (product: Product) => void;
  onOpenCatalog: () => void;
};

export const InterchangeRuleChain = ({
  members,
  categoryOf,
  priorityMode,
  sortIndexes,
  onPriorityModeChange,
  onAmountChange,
  onMove,
  onRemove,
  availableProducts,
  search,
  onSearchChange,
  attachInputRef,
  focusSearch,
  onAdd,
  onOpenCatalog,
}: Props) => {
  const { t } = useTranslation();
  const { isNarrow } = useInventoryShell();
  const [draggingKey, setDraggingKey] = useState<number | null>(null);
  const [dropKey, setDropKey] = useState<number | null>(null);

  // Приоритетът има смисъл от 3 продукта нагоре: при два всеки има само един
  // заместител и „кой пръв" не се пита. Номерата, стрелките и плъзгането —
  // само в режим „По реда тук".
  const showPriority = members.length >= 3;
  const reorderable = showPriority && priorityMode === "order";

  const focusAmount = (index: number) => {
    const member = members[index];
    if (!member) return false;
    document.getElementById(amountInputId(member.key))?.focus();
    return true;
  };

  const moveTo = (key: number, toIndex: number, focusId: string) => {
    onMove(key, toIndex);
    refocus(focusId, amountInputId(key));
  };

  const handleCardKeyDown = (event: KeyboardEvent<HTMLDivElement>, index: number) => {
    const member = members[index];
    const target = event.target as HTMLElement;
    if (event.altKey && (event.key === "ArrowUp" || event.key === "ArrowDown")) {
      if (!reorderable) return;
      event.preventDefault();
      const toIndex = index + (event.key === "ArrowUp" ? -1 : 1);
      if (toIndex < 0 || toIndex >= members.length) return;
      moveTo(member.key, toIndex, target.id || amountInputId(member.key));
      return;
    }
    if (target.id !== amountInputId(member.key)) return;
    if (event.ctrlKey || event.metaKey || event.altKey || event.shiftKey) return;
    if (event.key === "ArrowDown" || event.key === "Enter") {
      event.preventDefault();
      if (!focusAmount(index + 1)) focusSearch();
    } else if (event.key === "ArrowUp" && index > 0) {
      event.preventDefault();
      focusAmount(index - 1);
    }
  };

  const dragHandlers = (member: InterchangeMemberDraft, index: number) => ({
    onDragStart: (event: DragEvent<HTMLElement>) => {
      event.dataTransfer.effectAllowed = "move";
      event.dataTransfer.setData("text/plain", String(member.key));
      const card = event.currentTarget.closest<HTMLElement>("[data-ich-card]");
      if (card) event.dataTransfer.setDragImage(card, 24, 24);
      setDraggingKey(member.key);
    },
    onDragEnd: () => {
      setDraggingKey(null);
      setDropKey(null);
    },
    onDragOver: (event: DragEvent<HTMLDivElement>) => {
      if (draggingKey == null) return;
      event.preventDefault();
      event.dataTransfer.dropEffect = "move";
      if (dropKey !== member.key) setDropKey(member.key);
    },
    onDrop: (event: DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      if (draggingKey != null && draggingKey !== member.key) onMove(draggingKey, index);
      setDraggingKey(null);
      setDropKey(null);
    },
  });

  // „=" между картите — подравнено под количествата (с номер — по-навътре)
  const equalsOffsetClass = isNarrow
    ? reorderable
      ? "pl-[76px]"
      : "pl-10"
    : reorderable
      ? "pl-[94px]"
      : "pl-[54px]";
  const equals = () => (
    <li aria-hidden="true" className={`flex h-7 items-center ${equalsOffsetClass}`}>
      <span className="flex h-6 w-6 items-center justify-center rounded-full border border-shade-3 bg-canvas font-sheet text-sm font-bold leading-none text-muted">
        =
      </span>
    </li>
  );

  const slotHint =
    members.length === 0
      ? t("inventoryPage.ichSlotFirstHint")
      : members.length === 1
        ? t("inventoryPage.ichSlotSecondHint")
        : t("inventoryPage.ichSlotMoreHint");
  const slotLabel = (index: number, hint: ReactNode) => (
    <div className="mb-2 flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
      <span className="text-[10.5px] font-semibold uppercase leading-[normal] tracking-[0.08em] text-muted">
        {index > 2
          ? t("inventoryPage.ichSlotMoreLabel")
          : t("inventoryPage.ichSlotLabel", { index })}
      </span>
      <span className="text-[12.5px] leading-[normal] text-body">{hint}</span>
    </div>
  );

  return (
    // БЕЗ `overflow-hidden` — падащият списък на търсачката излиза извън картата
    <section className="rounded-xl border border-shade-2 bg-panel">
      <div className={isNarrow ? "px-3.5 pb-3 pt-3.5" : "px-[18px] pb-3.5 pt-4"}>
        <h2 className="m-0 text-base font-semibold leading-[1.2] text-ink">
          {t("inventoryPage.ichGroupTitle")}
        </h2>
        <p className="m-0 mt-1 max-w-[680px] text-[13px] leading-[1.45] text-body">
          {t("inventoryPage.ichGroupLead")}
        </p>
      </div>

      {showPriority && (
        <div
          className={`flex flex-wrap items-center gap-x-3 gap-y-2 border-t border-shade-2 ${
            isNarrow ? "px-3.5 py-3" : "px-[18px] py-3"
          }`}
        >
          <span className="text-[11px] font-semibold uppercase leading-[normal] tracking-[0.1em] text-muted">
            {t("inventoryPage.ichOrderLabel")}
          </span>
          <SegmentedCounts
            options={[
              { key: "oldest", label: t("inventoryPage.ichOrderOldest") },
              { key: "order", label: t("inventoryPage.ichOrderManual") },
            ]}
            value={priorityMode}
            onChange={onPriorityModeChange}
          />
          <span className="min-w-[200px] flex-1 text-[12.5px] leading-[1.4] text-muted">
            {priorityMode === "order"
              ? t("inventoryPage.ichOrderManualHelp")
              : t("inventoryPage.ichOrderOldestHelp")}
          </span>
        </div>
      )}

      <ol
        className={`m-0 flex list-none flex-col border-t border-shade-2 ${
          isNarrow ? "px-2.5 py-3" : "px-[18px] py-4"
        }`}
      >
        {members.map((member, index) => (
          <Fragment key={member.key}>
            {index > 0 && equals()}
            <li>
            <InterchangeMemberCard
              member={member}
              category={categoryOf(member.productId)}
              // номер само „По реда тук"; заредена смесена група без пипнат
              // ред показва „·" за продуктите без приоритет
              priority={
                reorderable ? { number: sortIndexes[index] != null ? index + 1 : null } : null
              }
              canMoveUp={index > 0}
              canMoveDown={index < members.length - 1}
              reorderable={reorderable}
              dragging={draggingKey === member.key}
              dropTarget={dropKey === member.key && draggingKey !== member.key}
              onAmountChange={(value) => onAmountChange(member.key, value)}
              onMoveUp={() => moveTo(member.key, index - 1, moveButtonId(member.key, "up"))}
              onMoveDown={() => moveTo(member.key, index + 1, moveButtonId(member.key, "down"))}
              moveButtonIds={{
                up: moveButtonId(member.key, "up"),
                down: moveButtonId(member.key, "down"),
              }}
              onRemove={() => {
                onRemove(member.key);
                // бутонът изчезва с картата — фокусът отива в търсачката (не
                // на телефон: там би отворил клавиатурата)
                if (!isNarrow) focusSearch();
              }}
              onKeyDown={(event) => handleCardKeyDown(event, index)}
              {...dragHandlers(member, index)}
            />
            </li>
          </Fragment>
        ))}
        {members.length > 0 && equals()}
        {/* Празното място е ВИНАГИ на тази позиция → търсачката не се
            монтира наново при добавяне и фокусът ѝ не се губи. */}
        <li>
          <div
            className={`rounded-[10px] border border-dashed border-shade-4 ${
              isNarrow ? "px-2.5 py-2.5" : "px-3.5 py-3"
            }`}
          >
            {slotLabel(members.length + 1, slotHint)}
            <InterchangeRuleSearch
              availableProducts={availableProducts}
              search={search}
              onSearchChange={onSearchChange}
              attachInputRef={attachInputRef}
              onAdd={onAdd}
              onOpenCatalog={onOpenCatalog}
              onArrowUpOut={() => focusAmount(members.length - 1)}
            />
          </div>
        </li>
        {members.length === 0 && (
          <>
            {equals()}
            <li>
              <div className="rounded-[10px] border border-dashed border-shade-3 px-3.5 py-3.5 opacity-70">
                {slotLabel(2, t("inventoryPage.ichSlotSecondHint"))}
              </div>
            </li>
          </>
        )}
      </ol>

      {members.length > 0 && !isNarrow && (
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 border-t border-shade-2 px-[18px] py-2.5 text-xs leading-[normal] text-muted">
          <span className="inline-flex items-center gap-1.5">
            <KbdChip>↑</KbdChip>
            <KbdChip>↓</KbdChip>
            <KbdChip>Enter</KbdChip>
            {t("inventoryPage.ichKeysAmounts")}
          </span>
          {reorderable && (
            <span className="inline-flex items-center gap-1.5">
              <KbdChip>Alt+↑</KbdChip>
              <KbdChip>Alt+↓</KbdChip>
              {t("inventoryPage.ichKeysMove")}
            </span>
          )}
        </div>
      )}
    </section>
  );
};

export default InterchangeRuleChain;
